import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

export interface TvAgingItem {
  materialCode: string;
  materialName: string;
  location: string;
  batchNo: string | null;
  quantityOnHand: number;
  ageDays: number | null;
}

interface TvItemAgingListProps {
  title: string;
  subtitle?: string;
  items: TvAgingItem[];
  columnLabels: { material: string; location: string; batch: string; qty: string; age: string };
}

const GRID_COLUMNS = '2.1fr 1.5fr 1fr 0.8fr 0.9fr';

/** Item-wise stock ageing list for the TV display — multi-column (unlike TvTopList's single
 * label/value/bar), since ageing needs material + location + batch + qty + age together to be
 * actionable at a glance. Age is color-coded (green/amber/red) so the oldest stock reads instantly
 * from across a room without anyone needing to read the exact number. */
export default function TvItemAgingList({ title, subtitle, items, columnLabels }: TvItemAgingListProps) {
  const theme = useTheme();

  const ageColor = (ageDays: number | null) => {
    if (ageDays == null) return 'rgba(255,255,255,0.5)';
    if (ageDays <= 30) return theme.palette.success.main;
    if (ageDays <= 90) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: '12px',
        bgcolor: alpha('#ffffff', 0.04),
        backdropFilter: 'blur(6px)',
        boxShadow: `inset 0 0 0 1px ${alpha('#ffffff', 0.06)}`,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack spacing={0.25} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
            {subtitle}
          </Typography>
        ) : undefined}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 1, px: 1, pb: 1 }}>
        {[columnLabels.material, columnLabels.location, columnLabels.batch, columnLabels.qty, columnLabels.age].map((label, i) => (
          <Typography
            key={label}
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: i >= 3 ? 'right' : 'left' }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Stack sx={{ overflow: 'hidden', flex: 1 }}>
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', px: 1 }}>
            —
          </Typography>
        ) : (
          items.map((item, i) => (
            <Box
              key={`${item.materialCode}-${item.batchNo ?? ''}-${item.location}-${i}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                gap: 1,
                alignItems: 'center',
                px: 1,
                py: 1,
                borderTop: i > 0 ? `1px solid ${alpha('#ffffff', 0.06)}` : 'none',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.materialCode}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                  {item.materialName}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.location}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.batchNo ?? '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'right' }}>
                {item.quantityOnHand.toLocaleString()}
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    px: 1,
                    py: 0.25,
                    borderRadius: '999px',
                    bgcolor: alpha(ageColor(item.ageDays), 0.18),
                  }}
                >
                  <Typography variant="body2" sx={{ color: ageColor(item.ageDays), fontWeight: 700 }}>
                    {item.ageDays != null ? `${item.ageDays}d` : '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Stack>
    </Box>
  );
}
